import { ImportStatus } from "@/types/fileImport.enum";
import { formatSeconds2MS } from "@/utils/formatter";

/**
 * 安全的日期解析函数
 * 处理各种日期格式的浏览器兼容性问题
 */
function parseDate(dateString: string): Date {
  if (!dateString) {
    return new Date();
  }

  // 尝试直接解析
  let date = new Date(dateString);
  
  // 如果解析失败，尝试标准化格式
  if (isNaN(date.getTime())) {
    // 处理 'YYYY-MM-DD HH:mm' 格式，转换为 ISO 格式
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(dateString)) {
      const isoString = dateString.replace(' ', 'T') + ':00';
      date = new Date(isoString);
    }
    
    // 处理 'YYYY-MM-DD HH:mm:ss' 格式
    else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      const isoString = dateString.replace(' ', 'T');
      date = new Date(isoString);
    }
    
    // 处理时间戳字符串
    else if (/^\d+$/.test(dateString)) {
      date = new Date(parseInt(dateString, 10));
    }
    
    // 其他格式，尝试手动解析
    else {
      const parts = dateString.match(/(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
      if (parts) {
        const [, year, month, day, hour = '0', minute = '0', second = '0'] = parts;
        date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1, // 月份从0开始
          parseInt(day, 10),
          parseInt(hour, 10),
          parseInt(minute, 10),
          parseInt(second, 10)
        );
      }
    }
  }
  
  // 如果仍然解析失败，返回当前时间
  if (isNaN(date.getTime())) {
    console.warn(`无法解析日期字符串: ${dateString}`);
    return new Date();
  }
  
  return date;
}

export const formatCreateDate = (createdDate: string): string => {
  const now = new Date();
  const givenTime = parseDate(createdDate);
  
  // 验证解析结果
  if (isNaN(givenTime.getTime())) {
    return "时间未知";
  }
  
  const diffInMilliseconds = now.getTime() - givenTime.getTime();
  
  // 处理未来时间的情况
  if (diffInMilliseconds < 0) {
    return "刚刚";
  }
  
  const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInHours < 1) {
    return "刚刚";
  } else if (diffInHours < 24) {
    return `${diffInHours}小时前`;
  } else if (diffInDays === 1) {
    return "1天前";
  } else if (diffInDays === 2) {
    return "2天前";
  } else if (diffInDays < 7) {
    const daysOfWeek = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
    return daysOfWeek[givenTime.getDay()];
  } else {
    const month = String(givenTime.getMonth() + 1).padStart(2, "0"); // 月份从0开始，需要加1，并且补零
    const day = String(givenTime.getDate()).padStart(2, "0"); // 补零
    return `${month}-${day}`;
  }
};

/**
 * 根据导入状态获取状态文本
 */
export const getStatusText = (
  status: ImportStatus,
  uploadProcess?: number,
  remainTime?: number
): string => {
  switch (status) {
    case ImportStatus.WAIT_UPLOAD:
      return '等待上传中...';
    case ImportStatus.UPLOADING:
      return `已上传（${uploadProcess ?? 0}%）`;
    case ImportStatus.WAITING:
      return '等待转写中...';
    case ImportStatus.TRANSING:
      return `转写中，预计剩余${formatSeconds2MS((remainTime ?? 0) / 1000)}`;
    case ImportStatus.TRANS_ERROR:
      return '转写错误';
    case ImportStatus.CANCEL:
      return '取消转写';
    case ImportStatus.UPLOAD_ERROR:
      return '上传失败';
    case ImportStatus.ADD_ERROR:
      return '转写失败';
    default:
      return '转写失败';
  }
};

/**
 * 判断是否为错误状态
 */
export const isErrorStatus = (status: ImportStatus): boolean => {
  return (
    status === ImportStatus.TRANS_ERROR ||
    status === ImportStatus.UPLOAD_ERROR ||
    status === ImportStatus.ADD_ERROR
  );
};
